import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getProfile, login as loginApi, logout as logoutApi } from "../../api/auth";
import { AuthTokens, AuthUser, LoginParams } from "../../types/auth";

export type AuthContextValue = {
  isAuthenticated: boolean;
  user: AuthUser | null;
  tokens: AuthTokens | null;
  login: (params: LoginParams) => Promise<void>;
  logout: () => void;
};

const SESSION_KEY = "kkp_admin_session_v1";

type StoredSession = {
  user?: AuthUser | null;
  tokens: AuthTokens;
  issuedAt?: string;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

function safeGetLocalStorageItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorageItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage errors (private mode, blocked, etc.)
  }
}

function safeRemoveLocalStorageItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function parseStoredSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as any;
    const tokens = parsed?.tokens ?? parsed;
    if (
      !tokens ||
      !tokens.access ||
      !tokens.refresh ||
      !String(tokens.access).trim() ||
      !String(tokens.refresh).trim()
    ) {
      return null;
    }
    const user = parsed?.user ?? null;
    const issuedAt = parsed?.issuedAt;
    return {
      user,
      tokens: {
        access: String(tokens.access),
        refresh: String(tokens.refresh),
      },
      issuedAt,
    };
  } catch {
    return null;
  }
}

function persistSession(session: StoredSession): void {
  safeSetLocalStorageItem(
    SESSION_KEY,
    JSON.stringify({
      user: session.user ?? null,
      tokens: {
        access: session.tokens.access,
        refresh: session.tokens.refresh,
      },
      issuedAt: session.issuedAt,
    }),
  );
}

function clearSession(): void {
  safeRemoveLocalStorageItem(SESSION_KEY);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<StoredSession | null>(() => {
    const raw = safeGetLocalStorageItem(SESSION_KEY);
    const parsed = parseStoredSession(raw);
    if (!parsed && raw) {
      clearSession();
    }
    return parsed;
  });

  const isAuthenticated = Boolean(
    session?.tokens.access && session?.tokens.refresh,
  );
  const tokens = session?.tokens ?? null;
  const user = tokens && session?.user ? session.user : null;

  const login = React.useCallback<AuthContextValue["login"]>(async (params) => {
    const u = params.username.trim();
    const p = params.password;

    const { tokens, user } = await loginApi({ username: u, password: p });
    if (!tokens?.access || !tokens?.refresh) {
      throw new Error("Missing auth tokens");
    }
    if (!user?.id || !user?.username || !user?.email) {
      throw new Error("Missing user profile");
    }

    const nextSession: StoredSession = {
      user: {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
      },
      tokens,
      issuedAt: new Date().toISOString(),
    };

    setSession(nextSession);
    persistSession(nextSession);
  }, []);

  const logout = React.useCallback(() => {
    const currentTokens = session?.tokens;
    if (currentTokens) {
      void logoutApi(currentTokens).catch(() => { });
    }
    setSession(null);
    clearSession();
  }, [session?.tokens]);

  React.useEffect(() => {
    let mounted = true;
    if (!isAuthenticated || !tokens) return;

    getProfile()
      .then((profile) => {
        if (!mounted) return;
        setSession((prev) => {
          if (!prev) return prev;
          const next: StoredSession = {
            ...prev,
            user: {
              id: profile.id,
              username: profile.username,
              email: profile.email,
              role: profile.role,
            },
          };
          persistSession(next);
          return next;
        });
      })
      .catch((err) => {
        // ignore profile failures in demo mode
        if (!mounted) return;
        console.warn("Profile fetch failed", err);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, tokens]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      user,
      tokens,
      login,
      logout,
    }),
    [isAuthenticated, user, tokens, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider />");
  }
  return ctx;
}

/**
 * Route guard for protected routes.
 *
 * Usage:
 * <Route element={<RequireAuth />}>
 *   <Route element={<DashboardLayout />}>
 *     ...
 *   </Route>
 * </Route>
 */
export function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <Outlet />;
}
